/**
 * clipboard-image.ts — put a PNG on the system clipboard, or fall back to a
 * download.
 *
 * Shared by the two "copy as image" paths: a board selection, which is drawn
 * onto a canvas from the object model (`board-raster.ts`), and a dashboard
 * chart, which is serialized out of the live SVG (`svg-raster.ts`). Both end up
 * holding a canvas, so everything after that point is the same and lives here.
 *
 * Three things about the Clipboard API drive the shape of this file.
 *
 * 1. **Safari requires the ClipboardItem to be built synchronously inside the
 *    user gesture.** `canvas.toBlob` is async, so the obvious
 *    `const blob = await toBlob(); clipboard.write([new ClipboardItem({...})])`
 *    has already left the gesture by the time it writes, and Safari rejects it
 *    with "NotAllowedError: Clipboard write is not allowed". The supported
 *    shape is to hand ClipboardItem the *promise* of a blob and let the browser
 *    await it, which keeps the write attributable to the click. Chrome and
 *    Firefox accept that form too, so there is one code path rather than a
 *    sniff.
 *
 * 2. **`ClipboardItem` may not exist at all.** Firefox only enabled it for
 *    writes relatively recently and it is absent in any non-secure context —
 *    and this app is served over plain http on a local tenant, where
 *    `window.isSecureContext` is true for localhost but false for a LAN IP.
 *    Someone demoing the portal from another machine on the network has no
 *    clipboard, so a copy that silently does nothing is the normal case rather
 *    than an edge one. Hence `downloadCanvas` and an outcome the caller can
 *    report honestly.
 *
 * 3. **Only `image/png` is reliably accepted.** Chrome rejects `image/jpeg`
 *    from `clipboard.write` outright, so there is no quality/size knob here.
 */

/** What actually happened, so the button can say so rather than guessing. */
export type CopyOutcome = 'copied' | 'downloaded' | 'failed'

/** True when this browser can be asked to write an image to the clipboard. */
export function canCopyImages(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard?.write
  )
}

/**
 * Promise of a PNG blob for a canvas.
 *
 * Deliberately NOT awaited by `copyCanvas` — see note 1 in the header. Rejects
 * rather than resolving null, because a ClipboardItem given a promise that
 * resolves to null fails with a message about the type rather than the cause.
 */
export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas could not be encoded as PNG'))
    }, 'image/png')
  })
}

/**
 * Copy a canvas to the clipboard as a PNG, falling back to a download.
 *
 * `filename` is used only by the fallback. Call this directly from the click
 * handler — anything awaited before it costs the user gesture on Safari.
 */
export async function copyCanvas(
  canvas: HTMLCanvasElement,
  filename: string,
): Promise<CopyOutcome> {
  if (canCopyImages()) {
    try {
      // The promise, not the blob. See note 1.
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': canvasToPngBlob(canvas) }),
      ])
      return 'copied'
    } catch {
      // Permission refused, a non-secure context that passed the feature test,
      // or a browser that dislikes the promise form. A download still gets the
      // image out, which is the thing the user asked for.
    }
  }
  return downloadCanvas(canvas, filename) ? 'downloaded' : 'failed'
}

/** Save the canvas as a PNG. Returns false if even that was refused. */
export function downloadCanvas(canvas: HTMLCanvasElement, filename: string): boolean {
  try {
    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith('.png') ? filename : `${filename}.png`
    document.body.appendChild(a)
    a.click()
    a.remove()
    return true
  } catch {
    return false
  }
}

/**
 * A filesystem-safe name for an exported image.
 *
 * Board titles and metric labels are free text and routinely carry `/`, `:` and
 * emoji, none of which belong in a download name. Everything outside a small
 * safe set collapses to a hyphen, runs of hyphens collapse to one, and an empty
 * result falls back rather than producing a file called `.png`.
 */
export function imageFilename(parts: Array<string | null | undefined>, fallback = 'image'): string {
  const slug = parts
    .filter((p): p is string => Boolean(p && p.trim()))
    .join('-')
    .normalize('NFKD')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\w.-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 80)
  return `${slug || fallback}.png`
}
